import { useState, useCallback } from 'react';
import { Home, CheckCircle2, ChevronLeft, ChevronRight, Upload, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { createAgentLoan, createLoan, deleteLoan, uploadAgentLoanDocument, uploadLoanDocument } from '../../../lib/api';
import { getLoanApplicationRoleConfig } from '../../../lib/loanApplicationFlow';
import { LOAN_PRODUCT_DOCUMENTS, buildLoanApplicationPayload, validateLoanApplicationStep, validateLoanApplicationSubmission } from '../../../lib/loanApplicationValidation';
import { toast } from 'sonner';

function Field({ label, required, error, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {label}{required ? <span className="text-rose-500 ml-0.5">*</span> : null}
            </Label>
            {children}
            {error ? (
                <p className="text-xs text-rose-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{error}
                </p>
            ) : null}
        </div>
    );
}

function FormInput({ id, type, label, placeholder, value, onChange, required, error }) {
    return (
        <Field label={label} required={required} error={error}>
            <Input id={id} type={type || 'text'} placeholder={placeholder} value={value} onChange={onChange} required={required}
                className={`h-11 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all ${error ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/20' : 'border-slate-200 dark:border-slate-700'}`} />
        </Field>
    );
}

function FormSelect({ id, label, options, value, onChange, required, error }) {
    return (
        <Field label={label} required={required} error={error}>
            <select id={id} value={value} onChange={onChange} required={required}
                className={`h-11 rounded-xl border px-3 text-sm w-full outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all ${error ? 'border-rose-500 focus:border-rose-500 focus:ring-rose-500/20' : 'border-slate-200 dark:border-slate-700'}`}>
                <option value="">Select…</option>
                {options.map(function (o) { return <option key={o.value} value={o.value}>{o.label}</option>; })}
            </select>
        </Field>
    );
}

function FileUploadField({ label, accept, onChange, file, error }) {
    return (
        <div className="flex flex-col gap-1.5">
            <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</Label>
            <label className={`flex flex-col items-center justify-center gap-2 h-24 border-2 border-dashed rounded-xl cursor-pointer bg-slate-50 dark:bg-slate-800/50 hover:bg-emerald-50/50 transition-all group ${error ? 'border-rose-500' : 'border-slate-200 dark:border-slate-700 hover:border-emerald-400'}`}>
                <input type="file" accept={accept} className="sr-only" onChange={onChange} />
                {file ? (
                    <div className="flex items-center gap-2 px-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                        <span className="text-xs text-emerald-600 font-medium truncate max-w-[160px]">{file.name}</span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-1">
                        <Upload className="h-5 w-5 text-slate-400 group-hover:text-emerald-500 transition-colors" />
                        <span className="text-xs text-slate-500">Click to upload</span>
                        <span className="text-[10px] text-slate-400">{accept}</span>
                    </div>
                )}
            </label>
            {error ? <p className="text-xs text-rose-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{error}</p> : null}
        </div>
    );
}

function ProgressBar({ steps, current }) {
    var pct = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;
    return (
        <div className="w-full">
            <div className="flex items-start justify-between mb-6">
                {steps.map(function (step, i) {
                    var done = i < current, active = i === current;
                    var cc = done ? 'bg-emerald-500 border-emerald-500 text-white' : active ? 'bg-white dark:bg-slate-900 border-emerald-500 text-emerald-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-400';
                    var lc = active ? 'text-emerald-600 dark:text-emerald-400' : done ? 'text-slate-600 dark:text-slate-400' : 'text-slate-400';
                    return (
                        <div key={i} className="flex-1 flex flex-col items-center relative">
                            {i < steps.length - 1 ? <div className={'absolute top-4 left-1/2 w-full h-0.5 transition-all duration-500 ' + (done ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700')} /> : null}
                            <div className={'relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-500 ' + cc}>
                                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                            </div>
                            <span className={'mt-2 text-[10px] font-medium text-center leading-tight max-w-[70px] ' + lc}>{step}</span>
                        </div>
                    );
                })}
            </div>
            <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-700" style={{ width: pct + '%' }} />
            </div>
            <div className="flex justify-between mt-1">
                <span className="text-[10px] text-slate-400">Step {current + 1} of {steps.length}</span>
                <span className="text-[10px] text-emerald-600 font-semibold">{pct}% Complete</span>
            </div>
        </div>
    );
}

function Step1({ d, u, errors }) {
    function c(k) { return { value: d[k], onChange: function (e) { u(k, e.target.value); } }; }
    return (
        <div className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
                <FormInput id="fullName" label="Full Name" placeholder="Ravi Kumar" required error={errors.fullName} {...c('fullName')} />
                <FormInput id="mobile" label="Mobile Number" type="tel" placeholder="+91 98765 43210" required error={errors.mobile} {...c('mobile')} />
                <FormInput id="email" label="Email Address" type="email" placeholder="ravi@example.com" required error={errors.email} {...c('email')} />
                <FormInput id="dob" label="Date of Birth" type="date" required error={errors.dob} {...c('dob')} />
                <FormInput id="pan" label="PAN Number" placeholder="ABCDE1234F" required error={errors.pan} {...c('pan')} />
                <FormInput id="aadhaar" label="Aadhaar Number" placeholder="1234 5678 9012" required error={errors.aadhaar} {...c('aadhaar')} />
            </div>
            <FormSelect id="empType" label="Employment Type" required error={errors.employmentType} options={[{ value: 'salaried', label: 'Salaried' }, { value: 'self_employed', label: 'Self Employed' }]} {...c('employmentType')} />
        </div>
    );
}

function Step2({ d, u, errors }) {
    function c(k) { return { value: d[k], onChange: function (e) { u(k, e.target.value); } }; }
    return (
        <div className="grid gap-5 sm:grid-cols-2">
            <FormInput id="income" label="Monthly Income (Rs.)" type="number" placeholder="75000" required error={errors.monthlyIncome} {...c('monthlyIncome')} />
            <FormInput id="company" label="Company / Business Name" placeholder="Infosys Ltd." required error={errors.companyName} {...c('companyName')} />
            <FormInput id="exp" label="Work Experience (Years)" type="number" placeholder="5" required error={errors.experience} {...c('experience')} />
        </div>
    );
}

function Step3({ d, u, errors }) {
    function c(k) { return { value: d[k], onChange: function (e) { u(k, e.target.value); } }; }
    return (
        <div className="grid gap-5 sm:grid-cols-2">
            <FormSelect id="propType" label="Property Type" required error={errors.propertyType} options={[{ value: 'flat', label: 'Flat / Apartment' }, { value: 'house', label: 'Independent House' }, { value: 'plot', label: 'Plot / Land' }]} {...c('propertyType')} />
            <FormInput id="propVal" label="Property Value (Rs.)" type="number" placeholder="6000000" required error={errors.propertyValue} {...c('propertyValue')} />
            <FormInput id="loanAmt" label="Loan Amount Required (Rs.)" type="number" placeholder="4500000" required error={errors.loanAmount} {...c('loanAmount')} />
            <FormInput id="propLoc" label="Property Location" placeholder="Andheri West, Mumbai" required error={errors.propertyLocation} {...c('propertyLocation')} />
        </div>
    );
}

function Step4({ files, onFile, errors }) {
    var docs = LOAN_PRODUCT_DOCUMENTS.home;
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            {docs.map(function (d) {
                return <FileUploadField key={d.key} label={d.label} accept={d.accept} error={errors[d.key]} file={files[d.key]} onChange={function (e) { onFile(d.key, e.target.files[0]); }} />;
            })}
        </div>
    );
}

function Step5({ basic, income, property }) {
    var rows = [
        ['Full Name', basic.fullName], ['Mobile', basic.mobile], ['Email', basic.email],
        ['PAN', basic.pan], ['Employment', basic.employmentType],
        ['Monthly Income', income.monthlyIncome ? 'Rs. ' + Number(income.monthlyIncome).toLocaleString('en-IN') : '-'],
        ['Company', income.companyName], ['Experience', income.experience ? income.experience + ' yrs' : '-'],
        ['Property Type', property.propertyType],
        ['Property Value', property.propertyValue ? 'Rs. ' + Number(property.propertyValue).toLocaleString('en-IN') : '-'],
        ['Loan Amount', property.loanAmount ? 'Rs. ' + Number(property.loanAmount).toLocaleString('en-IN') : '-'],
        ['Location', property.propertyLocation],
    ];
    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">Please review customer details before submitting.</p>
            <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                <table className="w-full text-sm"><tbody>
                    {rows.map(function (row, i) {
                        return (
                            <tr key={row[0]} className={i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800/40' : 'bg-white dark:bg-slate-900'}>
                                <td className="px-4 py-2.5 font-medium text-slate-600 dark:text-slate-400 w-1/2">{row[0]}</td>
                                <td className="px-4 py-2.5 text-slate-900 dark:text-slate-100">{row[1] || '-'}</td>
                            </tr>
                        );
                    })}
                </tbody></table>
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500">By submitting you agree to our Terms and Privacy Policy.</p>
        </div>
    );
}

var STEPS = ['Basic Details', 'Income Details', 'Property Details', 'Documents', 'Review & Submit'];

const AgentHomeLoan = ({ roleBasePath = '/agent' }) => {
    const roleConfig = getLoanApplicationRoleConfig(roleBasePath);
    const isAgentPortal = roleBasePath === '/agent';
    const createLoanRequest = isAgentPortal ? createAgentLoan : createLoan;
    const uploadLoanDocumentRequest = isAgentPortal ? uploadAgentLoanDocument : uploadLoanDocument;
    var sb = useState({ fullName: '', mobile: '', email: '', dob: '', pan: '', aadhaar: '', employmentType: '' });
    var basic = sb[0]; var setBasic = sb[1];
    var si = useState({ monthlyIncome: '', companyName: '', experience: '' });
    var income = si[0]; var setIncome = si[1];
    var sp = useState({ propertyType: '', propertyValue: '', loanAmount: '', propertyLocation: '' });
    var property = sp[0]; var setProperty = sp[1];
    var sf = useState({}); var files = sf[0]; var setFiles = sf[1];
    var sc = useState(0); var current = sc[0]; var setCurrent = sc[1];
    var ss = useState(false); var submitted = ss[0]; var setSubmitted = ss[1];
    var se = useState({}); var errors = se[0]; var setErrors = se[1];
    const { token } = useAuth();

    function clearError(k) { setErrors(function (p) { if (!p[k]) return p; var n = Object.assign({}, p); delete n[k]; return n; }); }
    function updBasic(k, v) { clearError(k); setBasic(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updIncome(k, v) { clearError(k); setIncome(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updProperty(k, v) { clearError(k); setProperty(function (p) { var n = Object.assign({}, p); n[k] = v; return n; }); }
    function updFile(k, f) { clearError(k); setFiles(function (p) { var n = Object.assign({}, p); n[k] = f; return n; }); }

    var isLast = current === STEPS.length - 1;
    async function handleNext() {
        var state = { basic: basic, income: income, property: property, files: files };
        if (!isLast) {
            var stepErrors = validateLoanApplicationStep('home', current, state);
            if (Object.keys(stepErrors).length > 0) {
                setErrors(stepErrors);
                toast.error('Please fix the highlighted fields.');
                return;
            }
            setErrors({});
        }
        if (isLast) {
            if (!token) {
                toast.error('Please log in to file this loan application.');
                return;
            }
            var validation = validateLoanApplicationSubmission('home', STEPS.length, state);
            if (!validation.success) {
                setCurrent(validation.stepIndex);
                setErrors(validation.errors);
                toast.error('Please fix the highlighted fields.');
                return;
            }
            let createdLoanId = null;
            try {
                // Step 1: Create the loan record (JSON — no files yet)
                const resp = await createLoanRequest(token, buildLoanApplicationPayload('home', state));

                const loanId = resp?.data?.id;
                createdLoanId = loanId;

                // Step 2: Upload each selected file against the new loan ID
                const fileEntries = Object.entries(files || {}).filter(([, f]) => f instanceof File);
                if (loanId && fileEntries.length > 0) {
                    toast.info(`Uploading ${fileEntries.length} document(s)…`);
                    const uploadResults = await Promise.allSettled(
                        fileEntries.map(([key, file]) => uploadLoanDocumentRequest(token, loanId, key, file))
                    );
                    const failedDocKeys = uploadResults.reduce((acc, result, index) => {
                        if (result.status === 'rejected') acc.push(fileEntries[index][0]);
                        return acc;
                    }, []);
                    if (failedDocKeys.length > 0) {
                        const firstFailure = uploadResults.find((result) => result.status === 'rejected');
                        const failureReason = firstFailure?.status === 'rejected' ? firstFailure.reason?.message : '';
                        throw new Error(
                            `Document upload failed for: ${failedDocKeys.join(', ')}${failureReason ? `. ${failureReason}` : ''}`
                        );
                    }
                }

                toast.success('Home loan application filed successfully.');
                setSubmitted(true);
            } catch (err) {
                let message = err?.message || 'Failed to file home loan application';
                if (createdLoanId) {
                    try {
                        await deleteLoan(token, createdLoanId, { rollbackUploadFailure: true });
                        message = `${message}. Created loan was rolled back.`;
                    } catch (rollbackError) {
                        const rollbackMessage = rollbackError?.message || 'Failed to roll back the created loan';
                        message = `${message}. Rollback failed: ${rollbackMessage}`;
                    }
                }
                toast.error(message);
            }
            return;
        }
        setCurrent(function (p) { return p + 1; });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    function handleBack() { setCurrent(function (p) { return p - 1; }); window.scrollTo({ top: 0, behavior: 'smooth' }); }

    var stepContent;
    if (current === 0) stepContent = <Step1 d={basic} u={updBasic} errors={errors} />;
    else if (current === 1) stepContent = <Step2 d={income} u={updIncome} errors={errors} />;
    else if (current === 2) stepContent = <Step3 d={property} u={updProperty} errors={errors} />;
    else if (current === 3) stepContent = <Step4 files={files} onFile={updFile} errors={errors} />;
    else stepContent = <Step5 basic={basic} income={income} property={property} />;

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center gap-4 mb-2">
                    <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <Home className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Home Loan Application</h1>
                        <p className="text-sm text-slate-500">{roleConfig.portalLabel}</p>
                    </div>
                </div>

                {submitted ? (
                    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-8 md:p-12 text-center space-y-5 shadow-sm">
                        <div className="h-24 w-24 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center animate-bounce mx-auto">
                            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">Application Filed successfully!</h2>
                        <p className="text-slate-600 dark:text-slate-400 max-w-sm mx-auto">The <strong>Home Loan</strong> application has been recorded. You can track its status in your dashboard.</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                            <Button asChild className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-8"><Link to={roleConfig.loansPath}>View All Loans</Link></Button>
                            <Button variant="outline" asChild className="rounded-xl px-8"><Link to={roleConfig.dashboardPath}>Dashboard</Link></Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-6">
                        <ProgressBar steps={STEPS} current={current} />
                        <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 md:p-8 space-y-6">
                            <div className="border-b border-slate-100 dark:border-slate-800 pb-4">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white font-['Plus_Jakarta_Sans']">{STEPS[current]}</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Fields marked with <span className="text-rose-500">*</span> are required.</p>
                            </div>
                            {stepContent}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="outline" onClick={handleBack} disabled={current === 0} className="rounded-xl gap-1.5"><ChevronLeft className="h-4 w-4" />Back</Button>
                                <Button onClick={handleNext} className="rounded-xl gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white px-6">
                                    {isLast ? 'Submit Application' : 'Continue'}
                                    {isLast ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentHomeLoan;
